import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Button from '../../../reusableComponents/buttons/button22'
import Label1 from '../../../reusableComponents/InputFields/Label1'
import ChangePasswordApi from '../../../../services/profileApi/ChangePasswordApi'
import { useAuth } from '../../../../context/AuthContext'

const ChangePasswordLabels = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    general: ''
  });
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  // Add this key to force re-render of Label1 components
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    if (currentUser && currentUser.email) {
      setEmail(currentUser.email);
    } else {
      // Fallback to stored email if currentUser is not yet available
      const storedEmail = localStorage.getItem('userEmail');
      if (storedEmail) {
        setEmail(storedEmail);
      }
    }
  }, [currentUser]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field when user types
    setErrors(prev => ({
      ...prev,
      [field]: '',
      general: ''
    }));
    setSuccess('');
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      general: ''
    };

    const { currentPassword, newPassword, confirmPassword } = formData;

    if (!currentPassword) {
      newErrors.currentPassword = 'Current password is required';
      isValid = false;
    }
    
    if (!newPassword) {
      newErrors.newPassword = 'New password is required';
      isValid = false;
    } else if (newPassword.length < 6) {
      newErrors.newPassword = 'New password must be at least 6 characters';
      isValid = false;
    }
    
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
      isValid = false;
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const { currentPassword, newPassword } = formData;
      const response = await ChangePasswordApi.updatePassword(
        email, 
        currentPassword, 
        newPassword
      );
      
      if (response.status === 'success') {
        setSuccess('Password updated successfully!');
        // Reset form
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        // Force re-render of Label1 components
        setResetKey(prev => prev + 1);
      } else {
        setErrors(prev => ({
          ...prev,
          general: response.message || 'Failed to update password'
        }));
      }
    } catch (err) {
      if (err.message?.includes('current password')) {
        setErrors(prev => ({
          ...prev,
          currentPassword: 'Current password is incorrect',
          general: ''
        }));
      } else {
        setErrors(prev => ({
          ...prev,
          general: err.message || 'An error occurred while updating password'
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form data
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setErrors({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      general: ''
    });
    setSuccess('');
    
    // Force re-render of Label1 components to clear their internal state
    setResetKey(prev => prev + 1);
    
    // Navigate back to profile page if needed
    // navigate('/profile');
  };

  return (
    <div className='flex flex-col gap-4'>
      <Label1 
        key={`currentPassword-${resetKey}`}
        label="Current Password"
        placeholder='Enter current password'
        value={formData.currentPassword}
        onChange={(value) => handleChange('currentPassword', value)}
        fullWidth={true}
        type="password"
        error={!!errors.currentPassword}
        errorText={errors.currentPassword}
        showSupportingText={!!errors.currentPassword}
      />
      <Label1 
        key={`newPassword-${resetKey}`}
        label="New Password"
        placeholder='Enter new password'
        value={formData.newPassword}
        onChange={(value) => handleChange('newPassword', value)}
        fullWidth={true}
        type="password"
        error={!!errors.newPassword}
        errorText={errors.newPassword}
        showSupportingText={!!errors.newPassword}
      />
      <Label1 
        key={`confirmPassword-${resetKey}`}
        label="Confirm Password"
        placeholder='Re-enter the password'
        value={formData.confirmPassword}
        onChange={(value) => handleChange('confirmPassword', value)}
        fullWidth={true}
        type="password"
        error={!!errors.confirmPassword}
        errorText={errors.confirmPassword}
        showSupportingText={!!errors.confirmPassword}
      />
      
      {errors.general && <div className="text-red-600 text-sm">{errors.general}</div>}
      {success && <div className="text-green-600 text-sm">{success}</div>}
      
      <div className='flex flex-row gap-[14px] justify-start'>
        <Button 
          text={loading ? "Saving..." : "Save Changes"}
          size="Compact"
          state={loading ? "Disabled" : "Hover"}
          type="Secondary"
          variant='Default'
          onClick={handleSave}
          disabled={loading}
        />
        <Button 
          text="Cancel"
          size="Compact"
          state="Default"
          type="Secondary"
          variant='Default'
          onClick={handleCancel}
          disabled={loading}
        />
      </div>
    </div>
  )
}

export default ChangePasswordLabels